import { Box, BoxProps, Card, Text } from '@mantine/core';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import React, { useState } from 'react';
import { HeatByPlace } from '../hooks';
import { DefaultCouncilInitializer } from './initialiser';
import { SuburbLayer } from './suburb-layer';
import { CustomSuburb } from './util';
import { MapViewLevelController } from './view-level-controller';

const GOOGLE_MAP_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_COUNCIL_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_COUNCIL_MAP_ID;

const NSW_CENTER = { lat: -31.2532, lng: 146.9211 };

type CouncilMapProps = {
	councilPlaceId: string;
	suburbPlaceIds: string[];
	customSuburbs: CustomSuburb[];
	onSuburbSelected: (placeId: string) => void;
	selectedSuburbPlaceId: string | null | undefined;
	heatOfPlaces: HeatByPlace;
} & BoxProps;

export const CouncilMap: React.FC<CouncilMapProps> = ({
	councilPlaceId,
	suburbPlaceIds,
	onSuburbSelected,
	selectedSuburbPlaceId,
	heatOfPlaces,
	customSuburbs,
	...props
}) => {
	const [activeViewport, setActiveViewport] = useState<google.maps.LatLngBounds | null>(null);

	if (!GOOGLE_MAP_API_KEY) {
		return (
			<Card shadow="sm" padding="lg" radius="md" withBorder>
				<Text c="red">
					Google Maps API key is not configured. Please set VITE_GOOGLE_MAPS_API_KEY environment
					variable.
				</Text>
			</Card>
		);
	}

	return (
		<Box h="100%" w="100%" pos="relative" {...props}>
			<APIProvider apiKey={GOOGLE_MAP_API_KEY}>
				<Map
					defaultCenter={NSW_CENTER}
					defaultZoom={7}
					mapId={GOOGLE_MAPS_COUNCIL_MAP_ID}
					style={{ height: '100%', width: '100%', borderRadius: '8px' }}
					mapTypeControl={false}
					streetViewControl={false}
					fullscreenControl={false}
					cameraControl={false}
					zoomControl
					renderingType="VECTOR"
				>
					<SuburbLayer
						activeViewport={activeViewport}
						councilPlaceId={councilPlaceId}
						suburbPlaceIds={suburbPlaceIds}
						onSuburbSelected={onSuburbSelected}
						selectedSuburbPlaceId={selectedSuburbPlaceId}
						heatOfPlaces={heatOfPlaces}
						customSuburbs={customSuburbs}
					/>

					<DefaultCouncilInitializer
						placeId={councilPlaceId}
						setActiveViewport={setActiveViewport}
					/>

					<MapViewLevelController viewport={activeViewport} />
				</Map>
			</APIProvider>
		</Box>
	);
};
